import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { G, errMsg } from "./guestApi";

const COLS = [["name", "Guest name"], ["phone", "Phone"], ["email", "Email"], ["notes", "Notes"]];
const blank = () => ({ name: "", phone: "", email: "", notes: "" });

function splitLines(text) {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
    const parts = l.includes("\t") ? l.split("\t") : l.split(/\s*,\s*/);
    if (parts.length === 1) {
      const m = l.match(/^(.*?)[\s:-]*(\+?[\d][\d\s-]{6,})\s*$/);
      return m ? { ...blank(), name: m[1].trim(), phone: m[2].trim() } : { ...blank(), phone: l };
    }
    const phoneIdx = parts.findIndex((p) => /\d{7,}/.test(p.replace(/\D/g, "")));
    const name = parts.find((p, i) => i !== phoneIdx && /[a-z]/i.test(p) && !p.includes("@")) || "";
    const email = parts.find((p) => p.includes("@")) || "";
    return { ...blank(), name: name.trim(), phone: (parts[phoneIdx] || "").trim(), email: email.trim() };
  });
}

export function BulkPasteDialog({ open, onClose, session, center, onStaged }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const parse = () => { setRows(splitLines(text)); };
  const edit = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const reset = () => { setText(""); setRows([]); };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${G}/import/from-rows`, { token: session.token, center, rows });
      toast.success("Rows staged — review before saving");
      onStaged(r.data.batch_id); reset(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="guest-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Bulk paste guests · {center}</DialogTitle>
          <DialogDescription>
            Paste from Excel/WhatsApp (one guest per line, e.g. “Asha Rao 98765 43210”). Edit the rows, then review.
          </DialogDescription>
        </DialogHeader>
        {rows.length === 0 ? (
          <>
            <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)}
              placeholder={"Asha Rao\t9876543210\nRavi, +61 412 345 678, ravi@mail.com"}
              data-testid="guest-bulk-textarea" />
            <DialogFooter>
              <Button onClick={parse} disabled={!text.trim()} data-testid="guest-bulk-parse">
                Turn into editable rows</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="max-h-[50vh] overflow-auto border rounded">
              <table className="w-full text-sm" data-testid="guest-bulk-grid">
                <thead className="bg-slate-50 text-xs"><tr>
                  <th className="p-1 w-8">#</th>
                  {COLS.map(([k, l]) => <th key={k} className="p-1 text-left">{l}</th>)}<th /></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-1 text-xs text-slate-400">{i + 1}</td>
                      {COLS.map(([k]) => (
                        <td key={k} className="p-1"><Input className="h-8" value={r[k]}
                          onChange={(e) => edit(i, k, e.target.value)}
                          data-testid={`guest-bulk-${k}-${i}`} /></td>))}
                      <td className="p-1"><Button size="sm" variant="ghost"
                        onClick={() => setRows(rows.filter((_, j) => j !== i))}
                        data-testid={`guest-bulk-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                    </tr>))}
                </tbody>
              </table>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setRows([...rows, blank()])}
                data-testid="guest-bulk-add-row"><Plus className="w-4 h-4 mr-1" />Row</Button>
              <Button variant="outline" onClick={reset} data-testid="guest-bulk-reset">Start over</Button>
              <Button onClick={submit} disabled={busy || rows.length === 0} data-testid="guest-bulk-submit">
                Review {rows.length} rows</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
